import useSWR from "swr";
import {
	AccountStatus,
	Answer,
	AnswerQuestionModel,
	CountryListRequest,
	GeoCountry,
	MasterAccountInfo,
	OCRResponse,
	ORCRequest,
	QuestionsResponse,
	SurveyType,
	TaxpayersResponse,
	UploadAddressProofsRequest,
	UpsertTaxpayerRequest,
} from "@/interfaces/surveyTypes";
import * as http from "@/utils/request";
import { RFResponse, RFResponseCommon, RFUpdateResponse } from "@/interfaces/common";
import { HasNonMainlandBankCardEnum } from "@/interfaces/enums";
import rfStore from "@/utils/rfStore";

const BASE_URL = process.env.NEXT_PUBLIC_BASE_API;

const sortRuleWithZHAcronym = ["SG", "CN", "HK", "TW", "MY", "NZ"];

export const useCountryList = ({ language = "en", smsAvailable = false, sticky = false }: CountryListRequest) => {
	const region = rfStore.getRFState()?.region;

	const url = `${BASE_URL}/geolocation/api/countries?language=${language}&smsAvailable=${smsAvailable}&sticky=${sticky}`;
	const {
		data = [],
		error,
		isLoading,
	} = useSWR(url, async (url) => await http.get<RFResponse<GeoCountry[]>>(url).then((res) => res.data));

	let res = data?.map((country) => {
		return { label: country.name, value: country.acronym };
	});
	// 通过语言排序
	if (language === "zh-Hans") {
		res = res.sort((a, b) => {
			const indexA = sortRuleWithZHAcronym.indexOf(a.value);
			const indexB = sortRuleWithZHAcronym.indexOf(b.value);
			if (indexA === -1 && indexB === -1) return 0;
			if (indexA === -1) return 1;
			if (indexB === -1) return -1;
			return indexA - indexB;
		});
	}

	// 通过地区排序 如果 value === region 则排在最前面
	res = res.sort((a, b) => {
		if (a.value === region) return -1;
		if (b.value === region) return 1;
		return 0;
	});

	return {
		countryList: res,
		data,
		error,
		isLoading,
	};
};

export const useCountryListByProvinceAndState = ({ acronym = "CN" }: { acronym: string }) => {
	const url = `${BASE_URL}/geolocation/api/countries/${acronym}/states`;
	const {
		data = [],
		error,
		isValidating,
	} = useSWR(url, async () => await http.get<RFResponse<GeoCountry[]>>(url).then((res) => res.data));
	const provinceAndStateList = data?.map((item) => {
		return { label: item.name, value: item.acronym };
	});

	return {
		provinceAndStateList,
		error,
	};
};

/**
 *  获取所有问卷问题
 */
export const useQuestions = ({
	type = SurveyType.ACCOUNT_OPENING,
	uid = rfStore.getRFState()?.userId,
}: {
	type: SurveyType;
	uid?: string;
}) => {
	const url = uid ? `${BASE_URL}/masterAccount/api/questions?type=${type}` : null;
	const { data, error, mutate, isLoading } = useSWR(
		url,
		async (url) => await http.get<RFResponse<QuestionsResponse>>(url).then((res) => res.data),
	);

	const questions = data?.questions || [];
	const questionFieldToIdMap = new Map();
	const questionFieldToUiSchemaMap = new Map();
	for (const question of questions) {
		questionFieldToIdMap.set(question.field, question.id);
		questionFieldToUiSchemaMap.set(question.field, question.uiSchema);
	}
	return {
		questionsLoading: !data,
		questions,
		questionFieldToIdMap,
		questionFieldToUiSchemaMap,
		error,
		mutate,
		isLoading,
	};
};

/**
 *  查询用户问卷答案
 */
export const useAnswers = ({ type = SurveyType.ACCOUNT_OPENING }: { type: SurveyType }) => {
	const url = `${BASE_URL}/masterAccount/api/questions/answers?questionType=${type}`;
	const uid = rfStore.getRFState()?.userId;
	const { data, error, mutate, isValidating, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponse<Answer[]>>(url).then((res) => res.data),
	);

	const answerFieldToValueMap = new Map();
	if (Array.isArray(data)) {
		for (const answer of data) {
			answerFieldToValueMap.set(answer.field, answer.answer);
		}
	}

	return {
		answersLoading: !data,
		answers: data,
		answerFieldToValueMap,
		error,
		mutate,
		isValidating,
		isLoading,
	};
};

/**
 * 查询纳税人信息
 */
export const useTaxpayers = () => {
	const url = `${BASE_URL}/masterAccount/api/taxPayers`;
	const { data, error, mutate } = useSWR(
		url,
		async (url) => await http.get<RFResponse<TaxpayersResponse>>(url).then((res) => res.data),
	);

	// TODO:
	return {
		taxResidences: data?.accountTaxPayers || [],
		mutate,
		loading: !data,
		error,
	};
};

/**
 *  同步手机号/邮箱
 */
export const useAccountStatus = () => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/identityInfo`;
	const { data } = useSWR(url, async (url) => await http.get<RFResponse<AccountStatus>>(url).then((res) => res.data));

	return {
		accountStatusLoading: !data,
		accountStatus: data,
	};
};

/**
 * 获取OCR信息
 *
 */
export const useOCR = () => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/ocr`;
	const { data, mutate } = useSWR(
		url,
		async (url) => await http.get<RFResponse<OCRResponse>>(url).then((res) => res.data),
	);

	return {
		OCR: data,
		mutate,
	};
};

/**
 * 获取MasterAccount 信息
 * ocrStatus 上传证件题叫
 * accountOpeningQuestionsAnswered 开户问提交
 * depositQuestionsAnswered 入金问题
 * addressProofStatus 地址证明
 */
export const useMasterAccount = () => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/status`;
	const { data, mutate, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponse<MasterAccountInfo>>(url).then((res) => res.data),
	);

	return {
		masterAccountLoading: !data,
		masterAccount: data,
		mutate,
		isLoading,
	};
};

export const checkMasterAccount = async () => {
	try {
		const url = `${BASE_URL}/masterAccount/api/masterAccounts/status`;
		const { data } = await http.get<RFResponse<MasterAccountInfo>>(url);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * 登录才请求接口
 */
export const useMasterAccountAndLogin = (isLogin: boolean) => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/status`;
	const { data, mutate } = useSWR(
		isLogin ? url : null,
		async (url) => await http.get<RFResponse<MasterAccountInfo>>(url).then((res) => res.data),
	);

	return {
		masterAccountLoading: !data,
		masterAccount: data,
		mutate,
	};
};

// ==============================================================
// =========================== 以下非SWR =========================
// ==============================================================

/**
 * 回答问题
 */
export const answerQuestions = async ({ payload = [] }: { payload: Array<AnswerQuestionModel> }) => {
	const url = `${BASE_URL}/masterAccount/api/questions/answers`;
	await http.post<Array<AnswerQuestionModel>, RFUpdateResponse>(url, payload);
};

/**
 * 回答问题
 * 搜索反馈问卷调查提交 [追加不覆盖]
 */
export const answerFeedBackQuestions = async ({
	questionType,
	payload = [],
}: {
	questionType: SurveyType;
	payload: Array<AnswerQuestionModel>;
}) => {
	const url = `${BASE_URL}/masterAccount/api/questions/answers/${questionType}`;
	await http.post<Array<AnswerQuestionModel>, RFUpdateResponse>(url, payload);
};

export const answerQuestionsWithNotOverWrite = async ({
	questionType,
	payload = [],
}: {
	questionType: SurveyType;
	payload: Array<AnswerQuestionModel>;
}) => {
	const url = `${BASE_URL}/masterAccount/api/questions/answers/replace/${questionType}`;
	await http.post<Array<AnswerQuestionModel>, RFUpdateResponse>(url, payload);
};

/**
 * 确认开户协议 / 确认金问题回答完了
 *  TODO: 入金问题回答完需要背后调用一下，通知Server问题已经回答完毕
 */
export const confirmAccountOpeningAgreement = async (type: SurveyType = SurveyType.ACCOUNT_OPENING) => {
	const url = `${BASE_URL}/masterAccount/api/questions/finished?questionType=${type}`;
	try {
		const { code } = await http.post<null, RFUpdateResponse>(url);
		if (code !== 200) {
			throw Error(String(code));
		}
		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * 上传地址证明
 */
export const uploadAddressProofs = async (payload: UploadAddressProofsRequest) => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/address/proofs`;
	const { code } = await http.post<UploadAddressProofsRequest, RFUpdateResponse>(url, payload);
};
/**
 * 创建/修改纳税人信息
 */
export const upsertTaxpayers = async (payload: UpsertTaxpayerRequest) => {
	const url = `${BASE_URL}/masterAccount/api/taxPayers`;
	return await http.post<UpsertTaxpayerRequest, RFUpdateResponse>(url, payload);
};

/**
 * 删除纳税人信息
 */
export const deleteTaxpayer = async (taxResidence: string) => {
	const url = `${BASE_URL}/masterAccount/api/taxPayers?taxResidence=${taxResidence}`;
	await http._delete<RFUpdateResponse>(url);
};

/**
 * 上传证件
 * @params{payload} ORCRequest
 *
 */
export const saveIdentityToORC = async (payload: ORCRequest) => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/ocr`;
	try {
		const { code, message } = await http.post<ORCRequest, RFResponseCommon<null>>(url, payload);
		if (code !== 200) {
			throw Error(message);
		}

		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * 判断是否为支持的国家
 * @params{acronym} 国家简称 eg： CN
 * @return{boolean}
 */
export const checkIsSafeCountry = async (acronym: string) => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/supportCountry/${acronym}`;
	return await http.get<RFResponse<boolean>>(url).then((res) => res.data);
};

/**
 * 户籍 || 居住地址 为CN
 * 提交 是否有境外银行卡
 * @param  {HasNonMainlandBankCardEnum}
 */
export const saveHasNonMainlandBankCard = async (status: HasNonMainlandBankCardEnum) => {
	const url = `${BASE_URL}/masterAccount/api/masterAccounts/hasNonMainlandBankCard/${status}`;
	return await http.post<null, RFResponseCommon<null>>(url).then((res) => res.data);
};

/**
 * 查用户是否完成 地址证明
 *
 */
export const useCheckAddressProofStatus = () => {
	const url = `${BASE_URL}/masterAccount/api/judge/partner`;
	const { data, isLoading } = useSWR(
		url,
		async (url) =>
			await http
				.get<
					RFResponse<{
						partnerUser: boolean; // 是否为三方用户
						hasAddress: boolean; // 是否有地址
					}>
				>(url)
				.then((res) => res.data),
	);

	const needExtraAddress = data?.partnerUser && !data?.hasAddress;
	return {
		data: needExtraAddress,
		isLoading,
	};
};

/**
 * 保存缺少的居住 + 并且同步到开户
 *
 */
export const saveMissingResidence = async (payload: { address: string }) => {
	const url = `${BASE_URL}/masterAccount/api/partner/account`;
	return await http.post<typeof payload, RFResponseCommon<null>>(url, payload);
};
